import { Component, EventEmitter, Input, Output } from '@angular/core';

import { TrayGroup } from '../payment-adjust-modal/payment-adjust-modal.component';

interface GrupoMeta {
	key: TrayGroup;
	label: string;
	icon: string;
	tono: 'danger' | 'warning' | 'muted';
	/** Qué es este grupo y qué se hace con él. Se pinta sobre la tabla. */
	explicacion: string;
	vacio: string;
	/** Lo que se despliega en el botón de ayuda, para quien llega sin contexto. */
	ayuda: {
		porQue: string;
		/** Cómo elegir entre las acciones posibles. Solo donde hay más de una. */
		criterios?: { pill: string; accion: string; porque: string }[];
		ojo: string;
	};
}

/**
 * Bandeja de pagos que necesitan atención.
 *
 * Es una **lista de trabajo**, no una acción: por eso vive en una pestaña con los contadores a la
 * vista y no escondida en un menú. Las fichas de la barra de Cobranza son a la vez el resumen y el
 * selector de grupo — así el operador ve cuánto dinero hay parado sin tener que entrar a buscarlo.
 */
@Component({
	selector: 'app-payment-attention-tab',
	templateUrl: './payment-attention-tab.component.html',
	styleUrls: ['./payment-attention-tab.component.scss'],
})
export class PaymentAttentionTabComponent {
	/** Respuesta de `GET /payments/needing-attention`. */
	@Input() data: any = null;
	@Input() loading = false;

	@Output() onSelect = new EventEmitter<{ row: any; group: TrayGroup }>();
	@Output() onReload = new EventEmitter<void>();

	/**
	 * Qué grupo se está mirando. Lo manda Cobranza desde `?grupo=`, no un clic de aquí dentro.
	 *
	 * Las fichas que lo eligen se mudaron a la barra de la vista: eran cuatro tarjetas de 90 px que
	 * empujaban esta tabla fuera de la pantalla para decir cuatro números.
	 */
	@Input() set group(value: TrayGroup) {
		this._group = value || 'remanentes';
	}
	get group(): TrayGroup {
		return this._group;
	}
	private _group: TrayGroup = 'remanentes';

	readonly grupos: GrupoMeta[] = [
		{
			key: 'remanentes',
			label: 'Con remanente',
			icon: 'fa-solid fa-coins',
			tono: 'warning',
			explicacion:
				'Les sobra un residuo que impide que queden aplicados por completo. Antes de cerrarlo revisa la columna de la derecha: si el arrendamiento todavía debe letras, ese saldo se APLICA por el flujo normal; cerrarlo aquí lo condonaría y el cliente perdería ese crédito.',
			vacio: 'Ningún pago arrastra remanentes.',
			ayuda: {
				porQue:
					'Al aplicar un pago a las letras casi siempre sobran unos centavos por redondeo. Ese residuo impide que el pago llegue a APLICADO, y se arrastra para siempre porque ninguna letra tiene exactamente ese importe.',
				criterios: [
					{
						pill: 'Aún se puede aplicar',
						accion: 'Aplícalo por el flujo normal',
						porque:
							'Su arrendamiento todavía debe letras. El pago ya aparece en el selector de aplicación, así que ese saldo se le puede acreditar al cliente. Condonarlo se lo quitaría.',
					},
					{
						pill: 'Sin letra pendiente',
						accion: 'Cerrar el remanente',
						porque: 'Todas las letras del arrendamiento están cubiertas: ese residuo no tiene a dónde ir y solo queda condonarlo.',
					},
				],
				ojo: 'Cerrar el remanente condona ese saldo: el cliente pierde ese crédito y ninguna letra lo recibe. Medido en producción, casi todos los remanentes SÍ tenían letra a la que aplicarse — por eso la herramienta bloquea cuando la hay. Y si lo que sobra pasa del umbral que capturas, avisa también: ahí ya no es redondeo, es dinero.',
			},
		},
		{
			key: 'posiblesDuplicados',
			label: 'Posibles duplicados',
			icon: 'fa-solid fa-clone',
			tono: 'warning',
			explicacion:
				'Pagos sin aplicar que tienen otro del mismo cliente por el mismo monto en la misma fecha o con la misma clave, dentro de la ventana de duplicados. Es evidencia, no veredicto: un cliente con dos arrendamientos paga la misma renta dos veces. Confirma contra el estado de cuenta y, solo si sobra, anúlalo desde aquí.',
			vacio: 'Ningún pago sin aplicar tiene un gemelo en la ventana de duplicados.',
			ayuda: {
				porQue:
					'Al registrar un pago la plataforma avisa si ya existe uno igual (mismo cliente, mismo monto, misma fecha o misma clave, últimos 30 días por default) y deja registrarlo con un motivo. Los que entraron así y siguen sin aplicar se juntan aquí para que alguien los revise antes de que se apliquen.',
				criterios: [
					{
						pill: 'Sobran pagos',
						accion: 'Puede haber duplicado',
						porque: 'Hay más pagos iguales que arrendamientos activos del cliente. Alguno se cargó de más — cuál, lo dice el estado de cuenta.',
					},
					{
						pill: 'Caben todos',
						accion: 'Probablemente NO sea duplicado',
						porque: 'El cliente tiene tantos arrendamientos como pagos iguales: lo normal es una renta por cada uno. Anular aquí borraría un pago legítimo.',
					},
				],
				ojo: 'La lista se arma con los gemelos del pago (misma forma que el aviso al registrar). Anular NO cancela aplicaciones porque estos pagos no tienen ninguna; si ya se aplicó, desaparece de aquí.',
			},
		},
		{
			key: 'anulados',
			label: 'Anulados aquí',
			icon: 'fa-solid fa-ban',
			tono: 'muted',
			explicacion:
				'Pagos que se sacaron de circulación desde esta herramienta. Fuera de esta lista ya no aparecen en ningún lado, así que es el único camino de vuelta: deshacer los devuelve exactamente al estado que la bitácora guardó antes del ajuste.',
			vacio: 'No se ha anulado ningún pago desde aquí.',
			ayuda: {
				porQue:
					'Un pago anulado desaparece del sistema: no sale en el listado de Pagos, ningún filtro lo encuentra y su ficha responde «no existe». Esta lista es la única forma de volver a él, y por eso existe.',
				ojo: 'Deshacer lo devuelve exactamente al estado que la bitácora guardó antes del ajuste, con quién lo hizo y por qué. Si el pago cambió después de esa anulación, te avisa bloqueando: revertir descartaría lo que pasó en medio.',
			},
		},
	];

	get resumen(): any {
		return this.data?.resumen ?? { remanentes: 0, montoRemanente: 0, anulados: 0, montoAnulado: 0, posiblesDuplicados: 0, montoPosibleDuplicado: 0 };
	}

	/** Conteo y monto de cada grupo, para los tiles. */
	conteo(key: TrayGroup): number {
		return this.resumen[key] ?? 0;
	}

	monto(key: TrayGroup): number {
		return { remanentes: this.resumen.montoRemanente, anulados: this.resumen.montoAnulado, posiblesDuplicados: this.resumen.montoPosibleDuplicado }[key] ?? 0;
	}

	get meta(): GrupoMeta {
		return this.grupos.find((g) => g.key === this.group) ?? this.grupos[0];
	}

	get rows(): any[] {
		return this.data?.[this.group] ?? [];
	}

	/** @deprecated Lo elige Cobranza por la dirección; se conserva por si algún camino viejo lo llama. */
	setGroup(key: TrayGroup): void {
		this._group = key;
	}

	/**
	 * Evidencia, no veredicto.
	 *
	 * Antes esta columna decía «Copia repetida» cuando existía otro pago con la misma clave y monto
	 * ya aplicado. Medido en producción, esa señal es falsa la mayoría de las veces: compartir clave
	 * y monto es la forma normal de un cliente con varios arrendamientos pagando la misma renta en
	 * cada uno. Ahora se muestran los dos números que sí permiten razonar y la decisión la toma
	 * quien tiene el estado de cuenta enfrente.
	 */
	evidencia(row: any): { iguales: number; activos: number; sobran: number } | null {
		if (this.group !== 'posiblesDuplicados') return null;

		//* Los pagos iguales salen de los gemelos del pago más el propio pago.
		const iguales = (row?.twins?.length ?? 0) + 1;
		const activos = row?.arrendamientosActivos ?? 0;
		return { iguales, activos, sobran: Math.max(0, iguales - activos) };
	}

	/** El detalle completo detrás de esos dos números. */
	evidenciaTooltip(row: any): string {
		const e = this.evidencia(row);
		if (!e) return '';
		if (e.iguales === 1) return 'No hay otro pago igual de este cliente en la ventana de duplicados.';

		//* Cuántos gemelos ya se aplicaron sale de los propios gemelos: el endpoint los devuelve con
		//* su estado. Antes se leía un campo que solo existía en el grupo que se retiró, así que este
		//* número decía «0 ya se aplicaron» pasara lo que pasara.
		const aplicados = (row?.twins ?? []).filter((t: any) => t?.status === 'APPLIED').length;
		const base = `${e.iguales} pagos iguales de este cliente en la ventana de duplicados; ${aplicados} ya se aplicaron. El cliente tiene ${e.activos} arrendamiento(s) activo(s).`;
		return e.sobran > 0
			? `${base} Sobran ${e.sobran}: es probable que alguno se haya cargado de más, pero confírmalo contra el estado de cuenta.`
			: `${base} Caben todos, así que este podría ser la renta de otro arrendamiento y no un duplicado.`;
	}

	/** A qué arrendamiento podría ir el saldo que sobra, y cuánto debe todavía. */
	destinoTooltip(row: any): string {
		const con = (row?.arrendamientos ?? []).filter((a: any) => a.letrasPendientes > 0);
		if (!con.length) return '';
		const money = (n: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n || 0);
		return `Se puede aplicar por el flujo normal: ${con.map((a: any) => `${a.folio} debe ${money(a.deuda)} en ${a.letrasPendientes} letra(s)`).join(' · ')}.`;
	}

	/** La cifra que le importa a cada grupo: lo que sobra, lo que falta por aplicar, o lo anulado. */
	cifra(row: any): number {
		if (this.group === 'remanentes') return row.remanente;
		if (this.group === 'posiblesDuplicados') return row.pendiente;
		return row.monto;
	}

	select(row: any): void {
		this.onSelect.emit({ row, group: this.group });
	}
}
